import {
  GridColDef,
  gridFilterModelSelector,
  gridPageSelector,
  gridPageSizeSelector,
  gridSortModelSelector,
} from "@mui/x-data-grid-pro";
import { EntityMappings } from "graphqlBase/__utils__/entityMappings";
import { SortEnumType } from "graphqlBase/types";
import { useEffect, useMemo } from "react";
import { isValidValue } from "./filterUtils";
import { EditableGridStateColDef, OnFilterOnSortCallBackProps } from "./types";

function onQueryParmsChange<E extends keyof EntityMappings>({
  query,
  apiRef,
  variables = {},
}: OnFilterOnSortCallBackProps<E>) {
  if (!apiRef?.current?.state) {
    query({ variables });
    return;
  }
  const take = variables?.take ?? gridPageSizeSelector(apiRef);
  const skip = gridPageSelector(apiRef) * take;
  const filterModel = gridFilterModelSelector(apiRef);
  const filterPre = filterModel.items.reduce<Array<EntityMappings[E]["filter"]>>(
    (where, { field, operator, value }) => {
      const column = apiRef?.current?.getColumn?.(field) as EditableGridStateColDef | undefined;
      if (!column?.remoteFilter?.filterPath || !operator || !column.type || !isValidValue(value, column.type))
        return where;
      const filterFunc = column.remoteFilter?.filterPath ?? function () {};

      return where.concat(
        filterFunc({
          where,
          filterValue: { [operator]: value },
        }) as EntityMappings[E]["filter"]
      );
    },
    []
  );
  const quickValues = (filterModel.quickFilterValues ?? []) as string[];

  const fuzzy = (apiRef?.current?.getVisibleColumns?.() as EditableGridStateColDef[]).reduce<unknown[]>(
    (where, { type, remoteFilter }) => {
      if (!quickValues.length) return where;
      if (type === "string" && remoteFilter) {
        const filterFunc = remoteFilter?.filterPath ?? function () {};
        const condition = quickValues.map((value) => ({ contains: value }));
        return where.concat(
          filterFunc({
            where,
            filterValue: { or: condition },
          }) as EntityMappings[E]["filter"]
        );
      }
      if (type === "stringArray" && remoteFilter) {
        const filterFunc = remoteFilter?.filterPath ?? function () {};
        const condition = { contains: quickValues[0] };
        return where.concat(
          filterFunc({
            where,
            filterValue: { or: condition },
          }) as EntityMappings[E]["filter"]
        );
      }
      return where;
    },
    []
  );

  const fuzzyFilter = { or: [...fuzzy] } as EntityMappings[E]["filter"];
  const filter = fuzzy.length ? (filterPre.length ? filterPre.concat(fuzzyFilter) : [fuzzyFilter]) : filterPre;

  const combinator = variables.where?.[filterModel.logicOperator ?? "and"];
  const nextWhere = filter.length ? [...(combinator ?? []), ...filter] : undefined;

  const where = nextWhere ? { ...variables.where, [filterModel.logicOperator ?? "and"]: nextWhere } : variables.where;

  const sort = gridSortModelSelector(apiRef).reduce<EntityMappings[E]["sort"]>((sorts, elem) => {
    const column = apiRef?.current?.getColumn?.(elem.field) as EditableGridStateColDef | undefined;
    if (!column?.remoteOrder || !elem.sort) return sorts;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return { ...sorts, ...(column.remoteOrder(elem.sort.toUpperCase() as SortEnumType) as any) };
  }, {});

  const sortInPre = variables?.order ?? {};
  const sortIn = Array.isArray(sortInPre) ? sortInPre : [sortInPre];

  const order = Object.entries(sort)
    .reduce<EntityMappings[E]["sort"][]>((sorts, [key, value]) => {
      return sorts.concat({ [key]: value });
    }, [])
    .concat(...sortIn);

  query({
    variables: { ...variables, take, skip, order, where },
  });
}
export function useExecuteQuery<E extends keyof EntityMappings>({
  apiRef,
  query,
  variables,
  enabled,
}: OnFilterOnSortCallBackProps<E>): () => void {
  const varsStr = JSON.stringify(variables);

  useEffect(() => {
    if (!enabled) return;
    onQueryParmsChange<E>({ query, apiRef, variables });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef, varsStr, query, enabled]);

  const executeQuery = useMemo(
    () => () => {
      if (!enabled) return;
      onQueryParmsChange<E>({ query, apiRef, variables });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiRef, varsStr, enabled]
  );
  return executeQuery;
}
